// biome-ignore lint/complexity/noStaticOnlyClass: keeping usage as class for consistency
export class ThemeEvents {
  static variantSelected =
    window.Theme?.events?.variantSelected || 'variant:selected'
  static variantUpdate = window.Theme?.events?.variantUpdate || 'variant:update'
  static cartUpdate = window.Theme?.events?.cartUpdate || 'cart:update'
  static cartError = window.Theme?.events?.cartError || 'cart:error'
  static cartDrawerOpen =
    window.Theme?.events?.cartDrawerOpen || 'cart:drawer-open'
  static mediaStartedPlaying =
    window.Theme?.events?.mediaStartedPlaying || 'media:started-playing'
  static quantitySelectorUpdate =
    window.Theme?.events?.quantitySelectorUpdate || 'quantity-selector:update'
  static bundleChange = window.Theme?.events?.bundleChange || 'bundle:change'
  static discountUpdate =
    window.Theme?.events?.discountUpdate || 'discount:update'
  static filterUpdate = window.Theme?.events?.filterUpdate || 'filter:update'
  static refresh = window.Theme?.events?.refresh || 'component:refresh'
  static scrollLockChanged =
    window.Theme?.events?.scrollLockChanged || 'scroll:lock'
  static modelColor = window.Theme?.events?.modelColor || 'model:color'
  static flavorChange = window.Theme?.events?.flavorChange || 'flavor:change'
  static slideshowSelect =
    window.Theme?.events?.slideshowSelect || 'slideshow:select'
  static sliderStateChange =
    window.Theme?.events?.sliderStateChange || 'slider-state-change'
  static videoPlay = window.Theme?.events?.videoPlay || 'video:play'
  static videoPause = window.Theme?.events?.videoPause || 'video:pause'
  static scroll = window.Theme?.events?.scroll || 'theme:scroll'
  static rawPreloaderReady = 'raw-preloader:ready'
}

export const rawPreloaderReadyClassName = 'raw-preloader-ready'

if (typeof window !== 'undefined') {
  window.Theme = window.Theme || {}
  window.Theme.events = ThemeEvents
}

const scrollLockClassName = 'nav-is-open'
const activeScrollLockSources = new Set()

function noop() {
  return undefined
}

function getScrollLockSource(source) {
  if (typeof source === 'string' && source.trim()) {
    return source
  }

  return 'global'
}

function hasScrollLockDetail(event) {
  return (
    event.detail !== null &&
    typeof event.detail === 'object' &&
    typeof event.detail.locked === 'boolean'
  )
}

function applyScrollLockDetail(detail) {
  const source = getScrollLockSource(detail.source)

  if (detail.locked) {
    activeScrollLockSources.add(source)
  } else {
    activeScrollLockSources.delete(source)
  }

  const shouldLock = activeScrollLockSources.size > 0
  document.documentElement.classList.toggle(scrollLockClassName, shouldLock)

  if (shouldLock) {
    window.lenis?.stop()
  } else {
    window.lenis?.start()
  }
}

function handleScrollLockChanged(event) {
  if (!hasScrollLockDetail(event)) return

  applyScrollLockDetail(event.detail)
}

export function on(eventName, callback, options, target = document) {
  target.addEventListener(eventName, callback, options)
  return () => target.removeEventListener(eventName, callback, options)
}

export function isRawPreloaderReady() {
  return (
    window.Shopify?.designMode === true ||
    document.documentElement.classList.contains(rawPreloaderReadyClassName) ||
    (document.readyState !== 'loading' &&
      !document.querySelector('raw-preloader'))
  )
}

export function onRawPreloaderReady(callback) {
  if (isRawPreloaderReady()) {
    callback(
      new RawPreloaderReadyEvent({
        reason: 'already-ready',
        skipped: true
      })
    )
    return noop
  }

  let completed = false
  let cleanupEvent = noop

  const complete = (event) => {
    if (completed) return
    completed = true
    cleanupEvent()
    document.removeEventListener('DOMContentLoaded', handleDomReady)
    callback(event)
  }

  const handleDomReady = () => {
    if (!isRawPreloaderReady()) return
    complete(
      new RawPreloaderReadyEvent({
        reason: 'missing-preloader',
        skipped: true
      })
    )
  }

  cleanupEvent = on(ThemeEvents.rawPreloaderReady, complete, {
    once: true
  })

  if (document.readyState !== 'loading') {
    return () => {
      completed = true
      cleanupEvent()
    }
  }

  document.addEventListener('DOMContentLoaded', handleDomReady, { once: true })

  return () => {
    completed = true
    cleanupEvent()
    document.removeEventListener('DOMContentLoaded', handleDomReady)
  }
}

const scrollDirectionThreshold = 5

export class ThemeScrollEvent extends CustomEvent {
  constructor(scrollY, direction, delta) {
    super(ThemeEvents.scroll, { detail: { scrollY, direction, delta } })
  }
}

const scrollSubscribers = new Set()
let scrollLastY = 0
let scrollLastDirection = 'up'
let scrollFrameId = 0

function readScroll() {
  scrollFrameId = 0
  const scrollY = Math.max(0, window.scrollY || 0)
  const delta = scrollY - scrollLastY

  if (Math.abs(delta) >= scrollDirectionThreshold) {
    scrollLastDirection = delta < 0 ? 'up' : 'down'
  }

  scrollLastY = scrollY
  document.dispatchEvent(
    new ThemeScrollEvent(scrollY, scrollLastDirection, delta)
  )
}

function handleScrollDispatch() {
  if (scrollFrameId) return
  scrollFrameId = window.requestAnimationFrame(readScroll)
}

function startScrollDispatcher() {
  if (scrollSubscribers.size !== 1) return
  scrollLastY = Math.max(0, window.scrollY || 0)
  window.addEventListener('scroll', handleScrollDispatch, { passive: true })
}

function stopScrollDispatcher() {
  if (scrollSubscribers.size !== 0) return
  window.removeEventListener('scroll', handleScrollDispatch)
  if (scrollFrameId) {
    window.cancelAnimationFrame(scrollFrameId)
    scrollFrameId = 0
  }
}

export function onThemeScroll(callback) {
  const off = on(ThemeEvents.scroll, callback)
  scrollSubscribers.add(callback)
  startScrollDispatcher()

  return () => {
    off()
    scrollSubscribers.delete(callback)
    stopScrollDispatcher()
  }
}

export class VariantSelectedEvent extends Event {
  constructor(resource) {
    super(ThemeEvents.variantSelected, { bubbles: true })
    this.detail = {
      resource
    }
  }
}

export class VariantUpdateEvent extends Event {
  constructor(resource, sourceId, data) {
    super(ThemeEvents.variantUpdate, { bubbles: true })
    this.detail = {
      data: {
        html: data.html,
        newProduct: data.newProduct,
        productId: data.productId
      },
      resource: resource || null,
      sourceId
    }
  }
}

export class CartAddEvent extends Event {
  constructor(resource, sourceId, data) {
    super(CartAddEvent.eventName, { bubbles: true })
    this.detail = {
      data: {
        ...data,
        changeType: data?.changeType ?? 'add'
      },
      isRewardAction: data?.isRewardAction,
      preventAutoOpen: data?.preventAutoOpen,
      resource,
      sourceId
    }
  }

  static eventName = ThemeEvents.cartUpdate
}

export class CartUpdateEvent extends Event {
  constructor(resource, sourceId, data) {
    super(ThemeEvents.cartUpdate, { bubbles: true })
    this.detail = {
      data: {
        ...data,
        changeType: data?.changeType ?? 'update'
      },
      isRewardAction: data?.isRewardAction,
      preventAutoOpen: data?.preventAutoOpen,
      resource,
      sourceId
    }
  }
}

export class CartErrorEvent extends Event {
  constructor(sourceId, message, description, errors) {
    super(ThemeEvents.cartError, { bubbles: true })
    this.detail = {
      data: {
        description,
        errors,
        message
      },
      sourceId
    }
  }
}

export class QuantitySelectorUpdateEvent extends Event {
  constructor(quantity, cartLine) {
    super(ThemeEvents.quantitySelectorUpdate, { bubbles: true })
    this.detail = {
      cartLine,
      quantity
    }
  }
}

export class BundleChangeEvent extends Event {
  constructor(total, target) {
    super(ThemeEvents.bundleChange, { bubbles: true })
    this.detail = {
      target,
      total
    }
  }
}

export class DiscountUpdateEvent extends Event {
  constructor(resource, sourceId, data) {
    super(ThemeEvents.discountUpdate, { bubbles: true })
    this.detail = {
      morphed: data?.morphed,
      preventAutoOpen: data?.preventAutoOpen,
      resource,
      sourceId
    }
  }
}

export class MediaStartedPlayingEvent extends Event {
  constructor(resource) {
    super(ThemeEvents.mediaStartedPlaying, { bubbles: true })
    this.detail = {
      resource
    }
  }
}

export class SlideshowSelectEvent extends Event {
  constructor(data) {
    super(SlideshowSelectEvent.eventName, { bubbles: true })
    this.detail = data
  }

  detail

  static eventName = ThemeEvents.slideshowSelect
}

export class FilterUpdateEvent extends Event {
  constructor(queryParams) {
    super(ThemeEvents.filterUpdate, { bubbles: true })
    this.detail = {
      queryParams
    }
  }

  shouldShowClearAll() {
    return (
      [...this.detail.queryParams.entries()].filter(([key]) =>
        key.startsWith('filter.')
      ).length > 0
    )
  }
}

export class ScrollLockChangedEvent extends CustomEvent {
  constructor(locked, source = 'global') {
    super(ThemeEvents.scrollLockChanged, {
      bubbles: true,
      detail: {
        locked: Boolean(locked),
        source: getScrollLockSource(source)
      }
    })
  }
}

export function scrollLock(locked, source = 'global') {
  const nextSource = getScrollLockSource(source)
  const nextLocked = Boolean(locked)

  if (activeScrollLockSources.has(nextSource) === nextLocked) return
  document.dispatchEvent(new ScrollLockChangedEvent(nextLocked, nextSource))
}

document.addEventListener(
  ThemeEvents.scrollLockChanged,
  handleScrollLockChanged
)

export class RefreshEvent extends CustomEvent {
  constructor(detail = {}) {
    super(ThemeEvents.refresh, {
      bubbles: true,
      composed: true,
      detail: {
        newContent: detail.newContent || null,
        targetElement: detail.targetElement || null
      }
    })
  }
}

export class RawPreloaderReadyEvent extends CustomEvent {
  constructor(detail = {}) {
    super(ThemeEvents.rawPreloaderReady, {
      bubbles: true,
      composed: true,
      detail: {
        desktop: Boolean(detail.desktop),
        reason: detail.reason || 'complete',
        skipped: Boolean(detail.skipped)
      }
    })
  }
}
